import type {
  AgencyCreatePayload,
  AgencyGraphJson,
  AgencyResponse,
  AgencyUpdatePayload,
  AgencyVisibility,
} from '@saaga/shared/interfaces/agency.interface'

import { filterKnownCategoryList } from '@/components/agencies/agency-category.util'
import type { AgencyFormSchemaType } from '@/components/agencies/agency-form.schema'

type AgencyContentFields = Required<Omit<AgencyCreatePayload, 'slug' | 'visibility'>>

const buildContentFields = (
  values: AgencyFormSchemaType,
  flowData: AgencyGraphJson
): AgencyContentFields => ({
  presentation_name: values.name,
  presentation_image_path: values.logoUrl || null,
  presentation_description_short: values.shortDescription || null,
  presentation_description_who_is_it_best_for: values.bestFor || null,
  presentation_description_detailed: values.longDescription || null,
  system_prompt_injected_to_all_nodes: values.systemPrompt || null,
  graph_json: { nodeList: flowData.nodeList, edgeList: flowData.edgeList },
  categories: values.categoryList ?? [],
})

export const mapFormToCreatePayload = (
  values: AgencyFormSchemaType,
  flowData: AgencyGraphJson
): AgencyCreatePayload => ({
  slug: values.slug,
  ...buildContentFields(values, flowData),
})

export const mapFormToUpdatePayload = (
  values: AgencyFormSchemaType,
  flowData: AgencyGraphJson
): AgencyUpdatePayload => buildContentFields(values, flowData)

export const buildVisibilityPayload = (visibility: AgencyVisibility): AgencyUpdatePayload => ({
  visibility,
})

export const mapResponseToFormValues = (
  response: AgencyResponse
): Partial<AgencyFormSchemaType> => ({
  slug: response.slug,
  name: response.presentation_name,
  logoUrl: response.presentation_image_path || null,
  shortDescription: response.presentation_description_short || '',
  longDescription: response.presentation_description_detailed || '',
  bestFor: response.presentation_description_who_is_it_best_for || '',
  systemPrompt: response.system_prompt_injected_to_all_nodes || '',
  categoryList: filterKnownCategoryList(response.categories ?? []),
  visibility: response.visibility,
  version: response.version,
})

export const extractAgencyGraphJsonFromResponse = (response: AgencyResponse): AgencyGraphJson => ({
  nodeList: response.graph_json?.nodeList ?? [],
  edgeList: response.graph_json?.edgeList ?? [],
})
